import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { multiSelectStyles } from "components/olitask/donations/credits/selectStyles";
import useRoles from "hooks/access-control/useRoles";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import CreateOrUpdateRoleModal from "./CreateOrUpdateRoleModal";
import AssignPrivilegesToRole from "./AssignPrivilegesToRole";
import { Button as ButtonAntd, Switch } from "antd";
import usePrivileges from "hooks/access-control/usePrivileges";
import SubtleBadge from "components/common/SubtleBadge";
import { toast } from "react-toastify";
import Guard from "../rbac/Guard";
import { PRIV } from "../rbac/privileges";

const getRoleAppLabel = (role) =>
  role?.app || role?.appName || role?.clientApp || "Not provided";

export default function RoleDetails() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const {
    fetchRoleById,
    updateRole,
    assignPrivilegesToRole,
    removePrivilegesFromRole,
    loading,
  } = useRoles();
  const { privileges } = usePrivileges();
  const [role, setRole] = useState(null);
  const [assignedPrivileges, setAssignedPrivileges] = useState([]);
  const [preSelectedPrivileges, setPreSelectedPrivileges] = useState([]);
  const [backupPrivileges, setBackupPrivileges] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [selectedPrivileges, setSelectedPrivileges] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [enableEdit, setEnableEdit] = useState(false);
  const [removedItemIds, setRemovedItemIds] = useState([]);
  const [originalPrivilegeIds, setOriginalPrivilegeIds] = useState([]);

  useEffect(() => {
    const fetchRole = async () => {
      try {
        const data = await fetchRoleById(roleId);
        setRole(data);

        // preselect privileges belonging to this role
        if (data?.privileges?.length) {
          const mapped = data.privileges.map((p) => ({
            value: p.id,
            label: p.name,
          }));
          setAssignedPrivileges(mapped);
          setPreSelectedPrivileges(mapped);

          // Save original assigned privilege IDs for comparison when revoking
          const original = data.privileges.map((p) => p.id);
          setOriginalPrivilegeIds(original);
        }
      } catch (error) {
        console.log("error occured", error);
      }
    };

    fetchRole();
  }, [roleId, fetchRoleById]);

  const handleSubmit = async () => {
    console.log("Selected privs", selectedPrivileges);
    if (selectedPrivileges.length === 0) {
      return toast.error("No new privileges selected");
    }

    setSubmitting(true);
    try {
      const data = await assignPrivilegesToRole(roleId, selectedPrivileges);
      if (data?.privileges?.length) {
        const mapped = data.privileges.map((p) => ({
          value: p.id,
          label: p.name,
        }));
        setPreSelectedPrivileges(mapped);
        setAssignedPrivileges([...assignedPrivileges, ...mapped]);
        setOriginalPrivilegeIds([
          ...originalPrivilegeIds,
          ...mapped.map((p) => p.value),
        ]);
        setSelectedPrivileges([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
      setDisabled(true);
    }
  };

  const handlePrivilegeChange = (newSelected) => {
    setPreSelectedPrivileges(newSelected);
    const currentIds = newSelected.map((p) => p.value);

    const removed = originalPrivilegeIds.filter(
      (id) => !currentIds.includes(id)
    );
    setRemovedItemIds(removed);
  };

  const handleEnableEdit = () => {
    setBackupPrivileges(assignedPrivileges);
    setEnableEdit(true);
  };

  const handleCancelEdit = () => {
    setAssignedPrivileges(backupPrivileges);
    setPreSelectedPrivileges(backupPrivileges); // restore original list
    setRemovedItemIds([]);
    setEnableEdit(false);
  };

  // remove privileges from role
  const handleRemovePrivileges = async () => {
    setSubmitting(true);
    try {
      await removePrivilegesFromRole(roleId, removedItemIds);

      // refresh ui list
      const updated = assignedPrivileges.filter(
        (p) => !removedItemIds.includes(p.value)
      );

      setAssignedPrivileges(updated);
      setPreSelectedPrivileges(updated);
      setOriginalPrivilegeIds(updated.map((p) => p.value));
    } catch (error) {
      console.log(error);
    } finally {
      setSubmitting(false);
      setEnableEdit(false);
      setRemovedItemIds([]);
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <Card className="shadow-none mt-1 p-0">
        <Card.Body className="d-flex align-items-center py-3 px-0 flex-wrap">
          <div className="d-flex align-items-center">
            <Button
              type="button"
              onClick={() => navigate("/access-control/roles")}
              size="sm"
              aria-label="Go back"
              variant="light"
            >
              <FontAwesomeIcon
                icon="chevron-left"
                transform="shrink-2"
                className="ms-1"
              />
            </Button>
          </div>

          <div className="px-3">
            <h4 className="mb-0 fw-semibold text-base">
              Manage ({role?.name}) Role
            </h4>
          </div>

          <div className="flex-1 d-flex mt-3 mt-sm-0 align-items-center justify-content-sm-end gap-2">
            <Guard privilege={PRIV.UPDATE_ROLES}>
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowModal(true)}
                className="text-nowrap text-xs"
              >
                Edit Role
                <FontAwesomeIcon
                  icon="edit"
                  transform="shrink-2"
                  className="ms-1"
                />
              </Button>
            </Guard>
          </div>
        </Card.Body>
      </Card>

      <Row className="py-lg-3 rounded-3 px-lg-4 relative gy-3">
        <Col xl={4} className="mb-2 mb-lg-0">
          <label className="form-label text-xxs mb-0">Name</label>
          <h6 className="mb-0 fw-semibold text-xxs col-10">
            {role?.name || "Not provided"}
          </h6>
        </Col>
        <Col xl={4} className="mb-2 mb-lg-0">
          <label className="form-label text-xxs mb-0">App</label>
          <h6 className="mb-0 fw-semibold text-xxs col-10">
            {getRoleAppLabel(role)}
          </h6>
        </Col>

        <Col xl={4} className="mb-2 mb-lg-0">
          <label className="form-label text-xxs mb-0">Description</label>
          <h6 className="mb-0 fw-semibold text-xxs col-10">
            {role?.description || "Not provided"}
          </h6>
        </Col>

        <Col className="mt-3 col-12">
          <div className="d-flex justify-content-between align-items-center">
            <p className="form-label text-xxs">Assigned Privileges</p>

            <div>
              {!enableEdit && assignedPrivileges.length > 0 && (
                <Guard privilege={PRIV.UPDATE_ROLES}>
                  <Button
                    variant="link"
                    className="text-xxs p-0 text-danger underline"
                    onClick={handleEnableEdit}
                  >
                    Remove privileges
                  </Button>
                </Guard>
              )}

              {enableEdit && (
                <>
                  <Button
                    variant="link"
                    className="text-xxs p-0"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          <AsyncSelect
            isMulti
            cacheOptions
            menuIsOpen={false}
            defaultOptions={role?.privileges?.map((p) => ({
              value: p.id,
              label: p.name,
            }))}
            value={preSelectedPrivileges}
            onChange={(options) => handlePrivilegeChange(options)}
            placeholder="No privileges | Assign privileges to role"
            isLoading={loading}
            styles={{
              ...multiSelectStyles,
              valueContainer: (base) => ({
                ...base,
                maxHeight: 255,
                overflowY: "auto",
              }),
            }}
            isDisabled={!enableEdit}
            isClearable={false}
            classNamePrefix="rs"
            className={`text-sm fw-semibold ${
              !enableEdit
                ? "scrollable-disabled-select text-sm fw-semibold"
                : ""
            }`}
          />

          {enableEdit && removedItemIds.length === 0 && (
            <p className="text-xxs mb-1 mt-2">Select privileges to remove</p>
          )}

          {enableEdit && removedItemIds.length > 0 && (
            <div className="mt-2">
              <p className="text-xxs text-danger mb-1">
                Privileges marked for removal:
              </p>
              <div className="text-sm text-danger">
                {removedItemIds.map((id) => {
                  const item = privileges.find((p) => p.id === id);
                  return (
                    <SubtleBadge pill key={id} className="me-2">
                      {item?.name}
                    </SubtleBadge>
                  );
                })}
              </div>

              <div className="d-flex align-items-end justify-content-between mt-3">
                <div>
                  <ButtonAntd
                    variant="secondary"
                    size="sm"
                    onClick={handleCancelEdit}
                    className="text-nowrap text-xxs bg-secondary text-white"
                  >
                    Cancel
                  </ButtonAntd>
                  {submitting ? (
                    <ButtonAntd
                      type="primary"
                      loading
                      iconPlacement="end"
                      size="sm"
                      className="ms-2 bg-success"
                      style={{ fontFamily: "Inter" }}
                    >
                      Saving
                    </ButtonAntd>
                  ) : (
                    <ButtonAntd
                      variant="success"
                      size="sm"
                      onClick={handleRemovePrivileges}
                      className="text-nowrap text-xxs ms-2 bg-success text-white"
                    >
                      Save
                    </ButtonAntd>
                  )}
                </div>
              </div>
            </div>
          )}
        </Col>

        <Col className="mt-4 col-12 mb-3 mb-lg-0 d-flex align-items-center justify-content-between">
          <Guard privilege={PRIV.ASSIGN_PRIVILEGES}>
            <Switch
              checked={!disabled}
              checkedChildren="Assign"
              unCheckedChildren="Enable"
              onClick={() => setDisabled((prev) => !prev)}
            />
          </Guard>
          {!disabled && (
            <div className="d-flex align-items-end justify-content-between">
              <div>
                <Button
                  variant="info"
                  size="sm"
                  onClick={() => setDisabled((prev) => !prev)}
                  className="text-nowrap text-xs"
                >
                  Cancel
                </Button>

                {submitting ? (
                  <ButtonAntd
                    type="primary"
                    loading
                    iconPlacement="end"
                    size="sm"
                    className="ms-2 bg-success"
                    style={{ fontFamily: "Inter" }}
                  >
                    Saving
                  </ButtonAntd>
                ) : (
                  <ButtonAntd
                    variant="success"
                    size="sm"
                    onClick={handleSubmit}
                    className="text-nowrap text-xs ms-2 bg-success text-white"
                  >
                    Save
                  </ButtonAntd>
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>

      <CreateOrUpdateRoleModal
        show={showModal}
        onHide={() => setShowModal(false)}
        role={role} // role to update
        onSave={updateRole}
      />

      <div className="px-lg-4">
        <AssignPrivilegesToRole
          role={role}
          disabled={disabled}
          onPrivilegesChange={(keys) => setSelectedPrivileges(keys)}
          assignedPrivileges={assignedPrivileges}
        />
      </div>
    </>
  );
}
